import React from 'react';
import { Info } from 'lucide-react';

interface V1FormRegistrationOptionsProps {
  allowRegistration: boolean;
  registrationPreSelected: boolean;
  registerOnlyWhenApproved: boolean;
  registrationInstructions?: string;
  isRegistrationChecked: boolean;
  onRegistrationChange: (checked: boolean) => void;
  isSubmissionApproved?: boolean;
  primaryColor?: string;
  customRegistrationActions?: {
    registerCallToAction?: string;
    finishRegistrationCallToAction?: string;
    registrationActionName?: string;
  };
}

/**
 * V1 Form Registration Options Component - FROZEN
 * 
 * Handles registration options for V1 forms:
 * - Allow Registration: Shows registration checkbox on form
 * - Registration Pre Selected: Pre-checks the registration option
 * - Register Only When Approved: Only creates registration if submission is approved
 * 
 * DO NOT add V2 features to this component.
 */
export const V1FormRegistrationOptions: React.FC<V1FormRegistrationOptionsProps> = ({
  allowRegistration,
  registrationPreSelected,
  registerOnlyWhenApproved,
  registrationInstructions,
  isRegistrationChecked,
  onRegistrationChange,
  isSubmissionApproved = false,
  primaryColor = '#3B82F6',
  customRegistrationActions
}) => {
  // Don't show if registration is not allowed
  if (!allowRegistration) {
    return null;
  }

  // Get custom naming or use defaults
  const registrationName = customRegistrationActions?.registrationActionName || 'Registration';
  const registerAction = customRegistrationActions?.registerCallToAction || 'Register';
  
  return (
    <div className="bg-gray-50 rounded-lg p-6 mb-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Event {registrationName}</h3>
      
      {/* Registration Instructions */}
      {registrationInstructions && (
        <div className="mb-4">
          <div 
            className="prose prose-sm max-w-none text-gray-700"
            dangerouslySetInnerHTML={{ __html: registrationInstructions }}
          />
        </div>
      )}
      
      {/* Registration Checkbox */}
      <div className="flex items-start">
        <input
          type="checkbox"
          id="register-for-event"
          checked={isRegistrationChecked}
          onChange={(e) => onRegistrationChange(e.target.checked)}
          className="mt-1 h-4 w-4 rounded border-gray-300 focus:ring-2 focus:ring-offset-2"
          style={{
            color: primaryColor,
            '--tw-ring-color': primaryColor
          } as React.CSSProperties}
        />
        <label htmlFor="register-for-event" className="ml-3 text-sm text-gray-700">
          <span className="font-medium">{registerAction} for this event</span>
        </label>
      </div>
      
      {/* Info Note */}
      <div className="mt-4 flex items-start text-sm text-gray-600">
        <Info className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
        <p>
          {registerOnlyWhenApproved 
            ? `Your ${registrationName.toLowerCase()} will be created after your submission is approved by the organizers.`
            : `Your ${registrationName.toLowerCase()} will be created immediately upon submission.`}
        </p>
      </div>
    </div>
  );
};